import { call, cancelled, put, select } from 'redux-saga/effects'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { clearAuth } from '@/containers/Login/AuthSlice'
import { showSnackbar, SNACKBAR_TYPES } from './notifications/notification'
import { isJsonResponse } from './nonJsonResponseError'
import { ipNotAllowedActions } from './ipNotAllowed'
import * as Sentry from '@sentry/react'
import { markReportedToSentry, REPORTED_BY_CALL_API } from './sentryBeforeSend'

// ─── Error codes NOT reported to Sentry ───
const SILENT_ERROR_CODES = new Set([
  'VALIDATION_ERROR',
  'NOT_FOUND',
  'UNAUTHENTICATED',
  'DUPLICATE_ENTRY',
  'SEARCH_FAILED',
  'BUSINESS_ERROR',
  'CALCULATION_FAILED',
  'SAVE_FAILED',
  'EXTERNAL_SERVICE_ERROR',
  'PROVIDER_UNAVAILABLE',
  'OPT_IN_SMS_TEXT_REQUIRED',
])

// ─── Structured error class ───
export class ApiError extends Error {
  constructor(code, message, { status = 0, details = null, validationErrors = {} } = {}) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
    this.validationErrors = validationErrors
  }

  get isValidation() {
    return this.code === 'VALIDATION_ERROR'
  }
  get isAuth() {
    return this.code === 'UNAUTHENTICATED'
  }
  get isNetwork() {
    return this.code === 'NETWORK_ERROR'
  }
  get isBusiness() {
    return !this.isValidation && !this.isAuth && !this.isNetwork
  }
}

// ─── Parse error response (standardized + Laravel formats) ───
function parseErrorResponse(data, status) {
  // Standardized format: { error: { code, message, details: { fields } } }
  if (data?.error && typeof data.error === 'object') {
    const err = data.error
    return new ApiError(err.code || 'UNKNOWN_ERROR', err.message || 'Unknown error.', {
      status,
      details: err.details || null,
      validationErrors: err.details?.fields || {},
    })
  }

  // Laravel format: { message, errors: { field: [...] } }
  const code = status === 422 ? 'VALIDATION_ERROR' : 'UNKNOWN_ERROR'
  const message = data?.message || data?.error || `Error ${status}`
  return new ApiError(code, message, {
    status,
    details: null,
    validationErrors: data?.errors || {},
  })
}

// ─── Smart Sentry capture ───
function captureToSentry(apiError, endpoint) {
  if (SILENT_ERROR_CODES.has(apiError.code)) {
    Sentry.addBreadcrumb({
      category: 'api.business_error',
      message: `${apiError.code}: ${apiError.message}`,
      level: 'info',
      data: { endpoint, details: apiError.details },
    })
    return
  }

  markReportedToSentry(apiError)
  Sentry.captureException(apiError, {
    tags: {
      ...REPORTED_BY_CALL_API,
      'api.error_code': apiError.code,
      'api.endpoint': endpoint,
    },
    extra: {
      status: apiError.status,
      details: apiError.details,
      validationErrors: apiError.validationErrors,
    },
  })
}

// ─── callApi v2 (standardized format: { success, data, error }) ───
export function* callApi({
  endpoint,
  method = 'GET',
  payload = null,
  queryParams = {},
  enqueue = null,
  showErrorSnackbar = true,
  asteriskReplacement = null,
}) {
  const controller = new AbortController()
  const { authTokenSession, country } = yield select(selectAuthData)

  try {
    const url = buildUrl(endpoint, country, queryParams, asteriskReplacement)
    const config = yield call(getAPIConfig, {
      method,
      authTokenSession,
      body: method !== 'GET' ? payload : null,
    })

    // ── 1. Network call ──
    let response
    try {
      response = yield call(fetch, url, {
        ...config,
        signal: controller.signal,
      })
    } catch {
      throw new ApiError('NETWORK_ERROR', 'Unable to reach the server.', { status: 0 })
    }

    // ── 2. Check Content-Type ──
    const contentType = response.headers.get('content-type') || ''
    if (!isJsonResponse(contentType)) {
      throw new ApiError('INVALID_RESPONSE', 'The server returned an unexpected response.', {
        status: response.status,
        details: { contentType },
      })
    }

    // ── 3. Parse JSON ──
    const data = yield call([response, response.json])

    // ── 4. Non-OK response → structured error ──
    if (!response.ok) {
      const ipRefusalActions = ipNotAllowedActions(response.status, data)
      if (ipRefusalActions !== null) {
        for (const action of ipRefusalActions) {
          yield put(action)
        }
        return
      }
      throw parseErrorResponse(data, response.status)
    }

    // ── 5. Extract data from standardized wrapper ──
    return data.data
  } catch (error) {
    const apiError =
      error instanceof ApiError
        ? error
        : new ApiError('UNKNOWN_ERROR', error.message || 'Unknown error.', {
            status: error.status || 0,
          })

    // ── 401 → logout ──
    if (apiError.status === 401 || apiError.isAuth) {
      yield put(clearAuth())
      if (enqueue) {
        yield call(showSnackbar, enqueue, {
          type: SNACKBAR_TYPES.ERROR,
          message: apiError.message || 'Session expired. Please log in again.',
        })
      }
      return
    }

    // ── Snackbar ──
    if (showErrorSnackbar && enqueue) {
      const snackMessage = apiError.isValidation
        ? 'Validation errors occurred. Please check the form fields.'
        : apiError.message
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.ERROR,
        message: snackMessage,
      })
    }

    // ── Sentry ──
    if (import.meta.env.PROD) {
      captureToSentry(apiError, endpoint)
    }

    throw apiError
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

const buildUrl = (endpoint, country, queryParams = {}, asteriskReplacement = null) => {
  if (!endpoint) {
    throw new Error('Configuration error. Missing endpoint URL. Please logout and login again')
  }

  let processedEndpoint = endpoint
  if (asteriskReplacement !== null && endpoint.includes('*')) {
    processedEndpoint = endpoint.replace('*', asteriskReplacement)
  }

  const hasParams = processedEndpoint.includes('?')
  const langParam = `lang=${country}`
  const queryString = new URLSearchParams(queryParams).toString()

  let url = processedEndpoint
  url += hasParams ? '&' + langParam : '?' + langParam
  if (queryString) url += '&' + queryString

  return url
}
